import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom, Subscription } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { StorageKeys, SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Utilities } from 'src/app/core/services/utilities';
import { GasLiftValve } from 'src/app/perical/models/gasLiftValve.model';
import { ThermalOperationsService } from 'src/app/perical/services/thermal-operations.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { WellConfigService } from 'src/app/shared/services/well-config.service';

@Component({
  selector: 'app-gas-lift-valves',
  templateUrl: './gas-lift-valves.component.html',
  styles: [`.input-100 span input {
    width: 100px;
  }`],
    providers: [DialogService, ConfirmationService],
    standalone: false
  })
export class GasLiftValvesComponent implements OnInit, OnDestroy {
  public columnDefs: any[];
  public tableHeight: string = '600px';

  private _gasLiftValves: GasLiftValve[];
  public selectedGasLiftValve: GasLiftValve;
  public gasLiftValvesForm: any;
  public gasLiftValveFg: any;
  public glvErrorMessage: string;
  private _designId: string;
  private _subscriptions: Subscription = new Subscription();
  private _glvsOrder: string[] = [];

  get gasLiftValves(): UntypedFormArray {
    return this.gasLiftValvesForm.get("gasLiftValves") as UntypedFormArray;
  }

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  // Validation
  public gasLiftValveDefaultDepth: number;
  public isLoading = signal<boolean>(false);
  public configMissing: boolean;

  // State
  @Input()
  public componentId: string;

  constructor(
    private _messenger: MediatorService,
    private _thermalOperationsService: ThermalOperationsService,
    private _formBuilder: UntypedFormBuilder,
    private _wellConfigService: WellConfigService,
    private _store: StoreService,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService
  ) {
    this.isLoading.set(true);

    this.gasLiftValvesForm = this._formBuilder.group({
      gasLiftValves: this._formBuilder.array([])
    });
  }

  async ngOnInit(): Promise<void> {
    this._designId = this._store.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS).designId;
    const uu = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);

    this.columnDefs = [
      { field: 'name', header: 'Name' },
      { field: 'measuredDepth', header: `Depth (${uu.longLengths} MD)` }
    ];

    this.getDataAndDefaults(false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  signalRfunc(data: { action: string, designId: string }) {
    if (data.action == PeriforOnChangeMessages.REFRESH_TUBULARS_LIST) {
      this.getDataAndDefaults(true);
    }
  }

  private getDataAndDefaults(isSignalR: boolean) {
    this._wellConfigService.getStringsList(this._designId).subscribe({
      next: (tubulars) => {
      if (tubulars.length > 0) {
        if (!tubulars[tubulars.length - 1].name.includes('Tubing')) {
          this.glvErrorMessage = 'Gas Lift Valves can only be added if tubing is specified.';
          this.isLoading.set(false);
          return;
        }
        this.getDefaults();
        this._subscriptions.add(this._thermalOperationsService.getGasLiftValves().subscribe(gasLiftValves => {
          this._gasLiftValves = gasLiftValves;
          this.populateFormData(isSignalR);
          this._glvsOrder = this._gasLiftValves.map(p => p.id);
        }));
      } else {
        this.configMissing = true;
        this.isLoading.set(false);
      }
    },
    error: () => {
      this.isLoading.set(false);
    }});
  }

  getDefaults() {
    this.isLoading.set(true);
    this._wellConfigService.getTubulars().subscribe({
      next: (tubulars) => {
      if (tubulars.length < 1) {
        this.configMissing = true;
        return;
      }

      this.configMissing = false;
      this.gasLiftValveDefaultDepth = tubulars[tubulars.length - 1].shoeMd;
      this.gasLiftValveFg?.controls.measuredDepth.setValidators(Validators.max(this.gasLiftValveDefaultDepth));
    },
    error: () => {
      this.isLoading.set(false);
      this.configMissing = true;
    }});
  }

  public onAddGlv(idx?: number): void {
    if (this.gasLiftValves.invalid) {
      return;
    }
    this.gasLiftValves.insert(idx || 0, this.newGasLiftValve(true));
  }

  public onDeleteGlv(idx: number): void {
    const glvId = this.gasLiftValves.controls[idx].value.id;
    this._confirmationService.confirm({
      message: 'Are you sure that you want to delete the selected Gas Lift Valve?',
      accept: async () => {
        if (glvId.length > 24) { // it's been saved to the db, so delete through API
            await lastValueFrom(this._thermalOperationsService.deleteGasLiftValve(glvId));
        } else {
          this._gasLiftValves = this._gasLiftValves.filter(val => val.id !== glvId);
        }

        this.gasLiftValves.removeAt(idx);
          this._messenger.publish(new DataSavedMessage('Gas Lift Valves'));
        }
    });
  }

  private populateFormData(isSignalR: boolean) {
    this._gasLiftValves.forEach((data, idx) => {
      const gasLiftValveFg = this.newGasLiftValve(false);
      if (!isSignalR) {
        this.gasLiftValves.push(gasLiftValveFg);
      }
      this.gasLiftValves.controls[idx].setValue(data, { emitEvent: false });
    });
    this.isLoading.set(false);
  }

    private newGasLiftValve(isNewGlv: boolean): UntypedFormGroup {
    this.gasLiftValveFg = new UntypedFormGroup({
      id: new UntypedFormControl(Utilities.generateFakeGuid()), // Setting a unique id here so that prime-ng p-table has a data key for the expand and collapse functionality.
      designId : new UntypedFormControl(this._designId),
      name: new UntypedFormControl(null, { validators: Validators.required, updateOn: 'blur' }),
      measuredDepth: new UntypedFormControl(this.gasLiftValveDefaultDepth, { updateOn: 'blur', validators: [Validators.required, Validators.max(this.gasLiftValveDefaultDepth)] })
    });

    this._subscriptions.add(this.gasLiftValveFg.valueChanges.subscribe((v) => this.handleSaveGlvRow(v, this.gasLiftValveFg)));

    if (isNewGlv) {
      this.gasLiftValveFg.controls.measuredDepth.patchValue(this.gasLiftValveFg.controls.measuredDepth.value);
    }

    this.gasLiftValveFg.markAllAsTouched();

    return this.gasLiftValveFg;
  }

  private async handleSaveGlvRow(v: any, gasLiftValveRecord: UntypedFormGroup): Promise<void> {
    if (gasLiftValveRecord.valid && !this.isLoading() && v.measuredDepth <= this.gasLiftValveDefaultDepth) {
      if (v.id.length == 24) { // it's only on the client so save to db as a new record
        await lastValueFrom(this._thermalOperationsService.setGasLiftValves(new GasLiftValve(v))).then(() => {
          this._glvsOrder.push(v.id);
          this.populateId();
          this._messenger.publish(new DataSavedMessage('Gas Lift Valves'));
        });
      } else {
        await lastValueFrom(this._thermalOperationsService.updateGasliftValves(new GasLiftValve(v))).then(() => {
          this._glvsOrder = this._glvsOrder.filter(id => id !== v.id);
          this._glvsOrder.push(v.id);
          this.populateId();
          this._messenger.publish(new DataSavedMessage('Gas Lift Valves'));
        });
      }
    }
  }

  private async populateId(): Promise<void> {
    await lastValueFrom(this._thermalOperationsService.getGasLiftValves()).then(glvs => {
      this.clearFormArray(this.gasLiftValves);
      this._gasLiftValves = glvs;

      this._gasLiftValves.sort((a, b) => {
        const indexA = this._glvsOrder.indexOf(a.id);
        const indexB = this._glvsOrder.indexOf(b.id);
        return indexA - indexB;
      });

      this.populateFormData(false);
    });
  }

    clearFormArray = (formArray: UntypedFormArray) => {
    while (formArray.length !== 0) {
      formArray.removeAt(0)
    }
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}